/**
 * The firm's own frameworks: the organising layer between the regimes it is
 * held to and the documents that answer them. A summary of the whole
 * programme, then one row per framework — coverage, open findings,
 * documents, health and lead — filtered by health and ordered worst first.
 * Each row opens the framework's page.
 *
 * @module routes/$org.compliance.frameworks.index
 */
import { createFileRoute } from "@tanstack/react-router";
import { SparklesIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { RegulationPageShell } from "../components/regulation/RegulationPageShell";
import {
  FrameworksEmpty,
  FrameworksListSkeleton,
  FrameworksListTable,
  FrameworksSummaryStrip,
  FrameworksToolbar,
  UnclaimedPanel,
} from "../components/regulation/frameworks/FrameworksList";
import {
  frameworkRows,
  frameworksSummary,
  healthCounts,
  visibleFrameworkRows,
  type FrameworkOrder,
  type HealthTab,
} from "../components/regulation/frameworks/frameworkView";
import type { RegimeCoverage } from "../components/regulation/monitor/frameworkRollup";
import { useRegulationOrg } from "../components/regulation/useRegulationOrg";
import { Button } from "../components/ui/button";
import { useFrameworks, useGovernanceFrameworks } from "../state/governance";
import { useOrgProfile } from "../state/orgProfile";
import { useRegulatoryUpdates } from "../state/policies";

function RegulationFrameworksIndexRoute() {
  const { org: orgSegment } = Route.useParams();
  const { org, isLoading } = useRegulationOrg();
  const organizationId = org?.id ?? null;
  const frameworks = useGovernanceFrameworks(organizationId);
  const { canManage } = useOrgProfile(organizationId);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [tab, setTab] = useState<HealthTab>("all");
  const [order, setOrder] = useState<FrameworkOrder>("health");
  const catalogue = useFrameworks(organizationId);
  const updates = useRegulatoryUpdates(organizationId);
  const list = frameworks.snapshot?.frameworks ?? [];
  const unorganised = frameworks.snapshot?.unorganisedRegimes ?? [];
  const unfiled = frameworks.snapshot?.unorganisedDocuments ?? [];
  const coverage = useMemo(
    () =>
      new Map(
        (catalogue.snapshot?.frameworks ?? []).map((regime): [string, RegimeCoverage] => [regime.id, regime]),
      ),
    [catalogue.snapshot],
  );
  // One clock reading per snapshot, so every row's activity window agrees.
  const rows = useMemo(
    () => frameworkRows(list, coverage, updates.snapshot?.updates ?? [], Date.now()),
    [list, coverage, updates.snapshot],
  );
  const counts = useMemo(() => healthCounts(rows), [rows]);
  const openFindingsTotal = frameworks.snapshot?.openFindings;
  const summary = useMemo(
    () => frameworksSummary(list, coverage, openFindingsTotal),
    [list, coverage, openFindingsTotal],
  );
  const loading = (frameworks.isPending && frameworks.snapshot === null) || (isLoading && organizationId === null);

  const organise =
    canManage && unorganised.length > 0 ? (
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setMessage(null);
          void frameworks.propose().then((failure) => {
            setBusy(false);
            setMessage(failure);
          });
        }}
        className="h-8 cursor-pointer gap-1.5 text-xs"
        title="One framework per domain of the regimes you enabled; rename, merge or split afterwards."
      >
        <SparklesIcon className="size-3.5" />
        Organise {unorganised.length} unclaimed {unorganised.length === 1 ? "regime" : "regimes"}
      </Button>
    ) : undefined;

  return (
    <RegulationPageShell
      title="Frameworks"
      description="How the firm organises itself to answer its regimes. Each framework owns documents, watches sources and collects what its regulators publish."
      actions={organise}
    >
      {(search) => {
        if (loading) {
          return (
            <div className="mt-6 pb-10">
              <FrameworksListSkeleton />
            </div>
          );
        }
        if (list.length === 0) {
          return (
            <div className="mt-6 space-y-4 pb-10">
              {message !== null ? <p role="alert" className="text-xs text-destructive">{message}</p> : null}
              <FrameworksEmpty title="No frameworks yet" action={organise}>
                {unorganised.length > 0
                  ? "Your regimes are not organised yet. Propose frameworks from them, then rename, merge or split what you get."
                  : "Enable a regime on the Regimes page; the frameworks that organise it start here."}
              </FrameworksEmpty>
              <UnclaimedPanel regimes={unorganised} documents={unfiled} orgSegment={orgSegment} />
            </div>
          );
        }
        const visible = visibleFrameworkRows(rows, { tab, query: search, order });
        return (
          <div className="mt-6 space-y-4 pb-10">
            {message !== null ? <p role="alert" className="text-xs text-destructive">{message}</p> : null}
            <FrameworksSummaryStrip summary={summary} />
            <FrameworksToolbar tab={tab} onTabChange={setTab} counts={counts} order={order} onOrderChange={setOrder} />
            {visible.length === 0 ? (
              <FrameworksEmpty
                icon="search"
                title={search.trim().length > 0 ? "No frameworks match that search" : "No frameworks here"}
                action={
                  tab !== "all" ? (
                    <Button type="button" size="xs" variant="outline" onClick={() => setTab("all")}>
                      Show all frameworks
                    </Button>
                  ) : undefined
                }
              >
                {search.trim().length > 0 ? "Search covers names, regimes, regulators and jurisdictions." : undefined}
              </FrameworksEmpty>
            ) : (
              <FrameworksListTable rows={visible} orgSegment={orgSegment} />
            )}
            <UnclaimedPanel regimes={unorganised} documents={unfiled} orgSegment={orgSegment} />
          </div>
        );
      }}
    </RegulationPageShell>
  );
}

export const Route = createFileRoute("/$org/compliance/frameworks/")({
  component: RegulationFrameworksIndexRoute,
});
